/**
 * Object/file view projection (RFC 0215: Log substrate).
 *
 * Derives the current set of objects on a branch from the `mod.object_*`
 * extension events (RFC 0215 §3.8), in topological order: created/edited set the
 * path, deleted removes it. This is the general framework that replaces RFC
 * 0206's file-specific materializer; RFC 0219 routes object/canvas lifecycle
 * through it. Object lifecycle is the first consumer of the extension envelope —
 * peers without an object handler load these entries, preserve them, and simply
 * do not project them here.
 */

import { topoOrder } from './projection-order'
import type { DocId, LogDoc } from './types'
import { OBJECT_EXTENSION } from './types'

export interface ObjectRef {
  path: string
  objectType: string
  docId: DocId
}

/** Current path -> object view, applying `mod.object_*` lifecycle in causal order. */
export function projectObjects(doc: LogDoc): Map<string, ObjectRef> {
  return projectObjectsWithTombstones(doc).view
}

/**
 * The `objectType` of an ordinary project file (RFC 0219 E1). Files reach a
 * peer through the branch materializer and the file share policy, not through
 * the session's own object set.
 */
export const FILE_OBJECT_TYPE = 'file'

/**
 * The docs a session MINTED, as opposed to the files it touched.
 *
 * This is what `session.harness.documents` used to hold: a PR snapshot, a plan,
 * a board instance. It was a mutable map on the session doc, written beside a
 * log that already recorded the same three facts (`path`, `objectType`,
 * `docId`) as causal `mod.object_*` events. The map is gone; this is the
 * projection that replaced it.
 *
 * Files are excluded because they have their own admission path, and a session
 * that edited a thousand files would otherwise drag a thousand content docs
 * into every reader's sync scope.
 */
export function projectSessionObjects(doc: LogDoc): ObjectRef[] {
  return [...projectObjects(doc).values()].filter(
    (ref) => ref.objectType !== FILE_OBJECT_TYPE,
  )
}

/**
 * Like `projectObjects`, but also returns the set of paths whose latest object
 * lifecycle event is a delete (tombstones). Callers that union projections
 * across sessions (the branch materializer) need the tombstones so a later
 * session's delete removes a path an earlier session created.
 */
export function projectObjectsWithTombstones(doc: LogDoc): {
  view: Map<string, ObjectRef>
  deleted: Set<string>
} {
  const view = new Map<string, ObjectRef>()
  const deleted = new Set<string>()
  for (const e of topoOrder(doc)) {
    const p = e.payload
    if (p.type !== 'extension') continue
    const { extensionType, payload } = p.payload
    // The extension payload is opaque (Record<string, unknown>); object events
    // carry { path, objectType, docId }. Coerce, since strings round-trip as
    // scalar string-likes through the codec.
    const path = payload.path != null ? String(payload.path) : undefined
    if (
      extensionType === OBJECT_EXTENSION.created ||
      extensionType === OBJECT_EXTENSION.edited
    ) {
      const docId =
        payload.docId != null ? (String(payload.docId) as DocId) : null
      if (path && docId != null) {
        view.set(path, {
          path,
          objectType:
            payload.objectType != null ? String(payload.objectType) : 'file',
          docId,
        })
        deleted.delete(path)
      }
    } else if (extensionType === OBJECT_EXTENSION.deleted) {
      if (path) {
        view.delete(path)
        deleted.add(path)
      }
    }
  }
  return { view, deleted }
}
